import React, { useRef, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Box,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CalendarRangePicker from "./CalendarRangePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers";

import { getFieldStyle, scrollToField } from "../fieldHelpers";
import { shouldDisableDate } from "../../services/helper";
import dayjs from "dayjs";
const OvertimeForm = ({
  formData,
  handleChange,
  fileNames,
  setFileNames,
  overtimeEntries,
  highlightedField,
  setOvertimeEntries,
  totalWorkingDays,
  setTotalWorkingDays,
  setFileObjects,
  fileObjects,
}) => {
  const fieldRefs = {
    projectType: useRef(null),
    subProjectType: useRef(null),
    leaveType: useRef(null),
    instructedBy: useRef(null),
    name: useRef(null),
    position: useRef(null),
    variationOrderNo: useRef(null),
    dateOfObservation: useRef(null),
    overtimeEntries: useRef(null),
    attachedFile: useRef(null),
    additionalNotes: useRef(null),
    status: useRef(null),
  };

  useEffect(() => {
    scrollToField(highlightedField, fieldRefs); // Use the external helper
  }, [highlightedField]);

  const uploadFile = (e) => {
    const file = e.target?.files[0];
    if (file) {
      setFileNames((prev) => [...prev, file.name]);
      setFileObjects((prev) => ({
        ...prev,
        [file.name]: file,
      }));
      handleChange(e);
    }
  };

  const previewFile = (fileName) => {
    const file = fileObjects[fileName];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, "_blank");
    } else {
      console.error("File not found for preview");
    }
  };

  const removeFile = (fileNameToRemove) => {
    setFileNames((prev) =>
      prev.filter((fileName) => fileName !== fileNameToRemove),
    );
    handleChange({ target: { name: "removeFile", value: fileNameToRemove } });
  };

  const addOvertimeEntry = () => {
    setOvertimeEntries((prev) => {
      const newEntries = [
        ...prev,
        {
          dateRange: [null, null],
          duration: {
            minutes: 0,
            hours: 0,
            days: 0,
          },
          repeatDate: "",
        },
      ];

      return newEntries;
    });
  };

  const removeOvertimeEntry = (index) => {
    setOvertimeEntries((prev) => prev.filter((_, i) => i !== index));
    handleChange({ target: { name: "removeOvertime", value: index } });
  };

  const handleDateChange = (index, event) => {
    const newDateRange = event.target.value;
    if (
      newDateRange &&
      Array.isArray(newDateRange) &&
      newDateRange.length > 1
    ) {
      const startDate = newDateRange[0];
      const endDate = newDateRange[1];

      if (startDate && endDate) {
        const differenceInMinutes = endDate.diff(startDate, "minute");
        const differenceInHours = endDate.diff(startDate, "hour");
        const differenceInDays = endDate.diff(startDate, "day");

        // Copy the overtimeEntries array to update only the selected entry
        const updatedEntries = [...overtimeEntries];

        // Check if the index is valid, and update only if within the bounds of existing entries
        if (index >= 0 && index < updatedEntries.length) {
          updatedEntries[index] = {
            ...updatedEntries[index], // Preserve other properties of the overtime entry
            dateRange: newDateRange, // Update dateRange
            duration: {
              minutes: differenceInMinutes % 60,
              hours: differenceInHours % 24,
              days: differenceInDays,
            },
          };

          // Update the overtime entries state with the modified entry
          setOvertimeEntries(updatedEntries);
          // Optionally, call handleChange to propagate changes to the parent component
          handleChange({
            target: {
              name: "addOvertime", // Custom name to identify the action
              value: updatedEntries, // Send updated overtime entries array
            },
          });
        } else {
          console.error("Invalid index for overtime entry update:", index);
        }
      }
    } else {
      const updatedEntries = [...overtimeEntries];
      updatedEntries[index] = {
        ...updatedEntries[index],
        repeatDate: newDateRange,
      };

      setOvertimeEntries(updatedEntries);
      handleChange({
        target: {
          name: "addOvertime", // Custom name to identify the action
          value: updatedEntries, // Send updated overtime entries array
        },
      });
    }
  };

  useEffect(() => {
    const total = overtimeEntries.reduce((acc, entry) => {
      const hoursInDays = entry.duration?.hours / 24;
      const minutesInDays = entry.duration?.minutes / 1440;

      const totalDays = entry.duration?.days + hoursInDays + minutesInDays;

      return acc + totalDays; // Accumulate the total working days
    }, 0);

    setTotalWorkingDays(total);
  }, [overtimeEntries, setTotalWorkingDays]);

  const formatWorkingTime = (decimalDays) => {
    const days = Math.floor(decimalDays); // Get the full days
    const hours = Math.floor((decimalDays - days) * 24); // Get the remaining hours
    const minutes = Math.round(((decimalDays - days) * 24 - hours) * 60); // Get the remaining minutes

    return `${days} day${days !== 1 ? "s" : ""}, ${hours} hr${hours !== 1 ? "s" : ""}, ${minutes} min${minutes !== 1 ? "s" : ""}`;
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {/* Project Selection */}
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Project
          </Typography>
          <FormControl
            ref={fieldRefs["projectType"]}
            fullWidth
            margin="normal"
            required
          >
            <Select
              labelId="project-type-label"
              name="projectType"
              value={formData.projectType || "prototype"} // Default to "prototype" if no value is selected
              onChange={handleChange}
              // inputRef={fieldRefs["projectType"]}
              sx={getFieldStyle("projectType", highlightedField)}
            >
              <MenuItem value="Swap Boiler">Swap Boiler</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
              <MenuItem value="prototype">Prototype</MenuItem>{" "}
              {/* Added Prototype option */}
            </Select>
          </FormControl>

          {/* Sub-Project Selection */}
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Sub-Project
          </Typography>
          <FormControl
            ref={fieldRefs["subProjectType"]}
            fullWidth
            margin="normal"
            required
          >
            <Select
              sx={getFieldStyle("subProjectType", highlightedField)}
              labelId="sub-project-type-label"
              name="subProjectType"
              value={formData.subProjectType || ""}
              onChange={handleChange}
            >
              <MenuItem value="Survey">Survey</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Leave Reason Selection */}
          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Reason
          </Typography>
          <FormControl
            ref={fieldRefs["leaveType"]}
            fullWidth
            margin="normal"
            required
          >
            <Select
              sx={getFieldStyle("leaveType", highlightedField)}
              labelId="leave-type-label"
              name="leaveType"
              value={formData.leaveType || ""}
              onChange={handleChange}
            >
              <MenuItem value="Extra Hours">Extra Hours</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Additional Fields */}
          <Typography variant="body2" sx={{ color: "#888", marginTop: "20px" }}>
            Instructed By
          </Typography>
          <TextField
            sx={getFieldStyle("instructedBy", highlightedField)}
            fullWidth
            margin="normal"
            name="instructedBy"
            value={formData.instructedBy || ""}
            onChange={handleChange}
            placeholder="Enter instructed by"
            inputRef={fieldRefs["instructedBy"]}
          />

          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Position
          </Typography>
          <TextField
            sx={getFieldStyle("position", highlightedField)}
            fullWidth
            margin="normal"
            name="position"
            value={formData.position || ""}
            onChange={handleChange}
            placeholder="Enter position"
            inputRef={fieldRefs["position"]}
          />

          <Typography variant="body2" sx={{ color: "#888", marginTop: "10px" }}>
            Variation Order No
          </Typography>
          <TextField
            sx={getFieldStyle("variationOrderNo", highlightedField)}
            fullWidth
            margin="normal"
            name="variationOrderNo"
            value={formData.variationOrderNo || ""}
            onChange={handleChange}
            placeholder="Enter Variation Order No"
            inputRef={fieldRefs["variationOrderNo"]}
          />

          {/* Date Range Picker for Instruction Received */}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <FormControl
              ref={fieldRefs["dateOfObservation"]}
              fullWidth
              margin="normal"
              required
            >
              <Typography
                variant="body2"
                sx={{ color: "#888", marginTop: "10px", marginBottom: "10px" }}
              >
                Instruction Received on (Date and Time)
              </Typography>
              <MobileDateTimePicker
                sx={{
                  ...getFieldStyle("dateOfObservation", highlightedField),
                  marginTop: "2.5px",
                }}
                // inputRef={fieldRefs["dateOfObservation"]}
                fullWidth
                margin="normal"
                name="dateOfObservation"
                shouldDisableDate={shouldDisableDate}
                value={
                  formData.dateOfObservation
                    ? dayjs(formData.dateOfObservation.$d) // Convert to Day.js object
                    : null
                }
                onChange={(newValue) =>
                  handleChange({
                    target: { name: "dateOfObservation", value: newValue },
                  })
                }
                format="DD/MM/YY hh:mm A" // Includes AM/PM
              />
            </FormControl>
          </LocalizationProvider>

          {/* Overtime Entries Section */}

          <Box
            ref={fieldRefs["overtimeEntries"]}
            sx={{
              marginTop: "20px",
              ...getFieldStyle("overtimeEntries", highlightedField),
            }}
          >
            <Typography variant="h6">Overtime Entries</Typography>
            {overtimeEntries.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center", // Align items vertically
                  justifyContent: "space-between", // Add space between picker and button
                }}
              >
                <CalendarRangePicker
                  formData={entry}
                  sx={getFieldStyle("overtimeEntries", highlightedField)}
                  handleChange={(newDateRange) =>
                    handleDateChange(index, newDateRange)
                  }
                  formatType="overtime"
                />
                <IconButton
                  color="error"
                  sx={{
                    marginLeft: "10px", // Space between picker and button
                    ...getFieldStyle("overtimeEntries", highlightedField), // Apply highlight style
                  }}
                  onClick={() => removeOvertimeEntry(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="contained"
              sx={{
                marginTop: "10px",
                width: "100%",
                ...getFieldStyle("overtimeEntries", highlightedField), // Apply highlight style to button
              }}
              onClick={addOvertimeEntry}
            >
              Add Overtime
            </Button>
          </Box>
          {/* Display Total Working Days */}
          <Typography
            variant="body2"
            sx={{
              marginTop: "10px", // Reduced spacing for compact layout
              textAlign: "center", // Keep it centered
              fontWeight: "500", // Slightly bold for the label
              color: "text.secondary", // Subtle color for the label
              letterSpacing: "0.5px", // Small spacing for clarity
            }}
          >
            Total Overtime
          </Typography>
          <Typography
            variant="body1"
            sx={{
              marginTop: "4px", // Reduced spacing for a compact look
              textAlign: "center", // Keep it centered
              fontWeight: "400", // Regular weight for a clean look
              color: "text.primary", // Standard text color for the value
              fontSize: "0.875rem", // Adjust the font size to make it smaller
            }}
          >
            {formatWorkingTime(totalWorkingDays)
              .split(" ")
              .map((item, index) => {
                if (isNaN(item)) {
                  return <span key={index}>{item} </span>;
                } else {
                  return (
                    <span
                      key={index}
                      style={{ color: "primary.main", fontWeight: "600" }}
                    >
                      {item}{" "}
                    </span>
                  );
                }
              })}
          </Typography>

          {/* File Upload Section */}
          <Button
            variant="outlined"
            component="label"
            sx={{ marginTop: "20px", display: "flex", alignItems: "center" }}
          >
            <AttachFileIcon sx={{ marginRight: "8px" }} />
            Add File
            <input
              type="file"
              hidden
              // style={getFieldStyle("attachedFile")}
              name="attachedFile"
              onChange={(e) => uploadFile(e)}
            />
          </Button>

          <FormControl
            ref={fieldRefs["attachedFile"]}
            fullWidth
            margin="normal"
            required
            sx={getFieldStyle("attachedFile", highlightedField)} // Apply highlight styles here
          >
            <Box sx={{ marginTop: "10px" }}>
              {fileNames.map((fileName, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 10px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    marginTop: "5px",
                  }}
                >
                  <Typography variant="body2">{fileName}</Typography>
                  <Box>
                    <Button
                      color="primary"
                      size="small"
                      sx={{ marginRight: "8px" }}
                      onClick={() => previewFile(fileName)}
                    >
                      Preview
                    </Button>
                    <Button
                      color="error"
                      size="small"
                      onClick={() => removeFile(fileName)}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </FormControl>
        </Grid>
      </Grid>

      <FormControl
        ref={fieldRefs["additionalNotes"]}
        fullWidth
        margin="normal"
        required
        sx={getFieldStyle("additionalNotes", highlightedField)} // Apply highlight styles here
      >
        <TextField
          fullWidth
          margin="normal"
          label="Additional Notes"
          multiline
          rows={4}
          name="additionalNotes"
          value={formData.additionalNotes || ""}
          onChange={handleChange}
        />
      </FormControl>
    </>
  );
};

export default OvertimeForm;
